import React, { ChangeEvent, FunctionComponent, useEffect, useState } from 'react'
import { Table, Typography, Col, Row, notification, Input, Space, Button, Select } from 'antd'
import { FilterValue, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface'
import { DownloadOutlined } from '@ant-design/icons'
import classNames from 'classnames'

import { DEFAULT_PAGE_SIZE } from '../../../../constants'
import { getCallsList } from '../../../../context/admin/utils'
import { Call, TCallLog } from '../../../../context/types'
import { useAuthContext } from '../../../../context/auth'
import { columns } from './columns'
import './calls.less'
import CustomModal from '../../../helpers/CustomModal'
import CallLogsModal from './CallLogsModal'

const { Search } = Input
const { Option } = Select
const { Title } = Typography

interface AdminCallsProps {
	state?: number
}

const statuses: string[] = ['All', 'Upcoming', 'Completed']

const Calls: FunctionComponent<AdminCallsProps> = (props) => {
	const { state } = props
	const { auth } = useAuthContext()
	const [loading, setLoading] = useState(false)
	const [filter, setFilter] = useState('')
	const [status, setStatus] = useState('All')
	const [page, setPage] = useState(1)
	const [column, setColumn] = useState('call.start_time')
	const [order, setOrder] = useState<Order>('descend')
	const [total, setTotal] = useState(10)
	const [calls, setCalls] = useState<Call[]>([])
	const [callLogs, setCallLogs] = useState<TCallLog[] | null>(null)

	useEffect(() => {
		setLoading(true)
		getCallsList((page - 1) * DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE, column, order, filter, status)
			.then((r) => {
				setCalls(r.list)
				setTotal(r.total)
			})
			.catch(handleError)
			.finally(() => setLoading(false))
	}, [page, order, column, state, filter, status])

	const handleError = (err: any) => {
		notification.error({ message: err.message || 'Server error' })
	}
	const handleSetFilter = (e: ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)

	const onChange: OnChange = (pagination, _, sorter) => {
		const customSorter = sorter as SorterResult<Call>

		setOrder(customSorter.order as Order)
		setColumn(customSorter.columnKey as string)
		setPage(pagination.current)
	}

	const handleStatusChange = (value: string) => {
		setPage(1)
		setStatus(value)
	}

	return (
		<>
			<Space direction="vertical" style={{ width: '100%' }}>
				<Row>
					<Col xs={18} md={8} lg={11}>
						<Title level={3}>Calls</Title>
					</Col>
					<Col xs={6} md={3} lg={2}>
						<Button
							type="primary"
							shape="round"
							icon={<DownloadOutlined />}
							size="middle"
							style={{ height: 32, lineHeight: '32px' }}
							href={`${process.env.REACT_APP_API_URL || '/'}api/admin/users/calls/download?token=${
								auth.token
							}&status=${status}`}
							target="_blank"
							download
						/>
					</Col>
					<Col xs={10} md={5} lg={3}>
						<Select defaultValue={status} style={{ width: '95%' }} onChange={handleStatusChange}>
							{statuses.map((v, i) => (
								<Option key={i} value={v}>
									{v}
								</Option>
							))}
						</Select>
					</Col>
					<Col xs={14} md={8} lg={8}>
						<Search allowClear placeholder="Filter" value={filter} onChange={handleSetFilter} onSearch={setFilter} />
					</Col>
				</Row>
				<Table
					className="admin-calls-table"
					rowKey={'id'}
					rowClassName={({ status }: Call) =>
						classNames({
							'past-both-joined-call': !status.upcoming && status.bothJoined,
							'past-no-join-call': !status.upcoming && !status.bothJoined,
						})
					}
					columns={columns((callLogs: TCallLog[]) => setCallLogs(callLogs))}
					dataSource={calls}
					pagination={{ pageSize: DEFAULT_PAGE_SIZE, current: page, total }}
					loading={loading}
					onChange={onChange}
					scroll={{ x: true }}
				/>
			</Space>

			<CustomModal
				centered
				visible={callLogs && !!callLogs.length}
				width={900}
				onCancel={() => setCallLogs(null)}
				style={{ height: '80%' }}
				bodyStyle={{ overflowY: 'scroll', height: '75vh' }}
			>
				<CallLogsModal data={callLogs} />
			</CustomModal>
		</>
	)
}

export default Calls

type Order = 'ascend' | 'descend' | undefined
type OnChange = (
	pagination: TablePaginationConfig,
	filters: Record<string, FilterValue | null>,
	sorter: SorterResult<any> | SorterResult<any>[]
) => void
